// AdminAuditLog — admin-only cross-tenant audit log viewer.
//
// Route: /admin/audit-log, gated by RequireAuth role="admin". Backed by
// GET /v1/admin/audit-log (admin-only). Surfaces every audit_log row
// across the platform with filters for the common forensic queries
// ("everything user X did", "everything that happened to payout 518",
// "every approval in the last hour").
//
// Each row renders the action + actor + IP + request_id inline; the
// row's before/after JSON drops into an expandable section so the page
// stays readable at hundreds of rows while preserving full forensic
// detail one click away.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Pill } from "@/components/UI";
import {
  listAdminAuditLog,
  type AdminAuditLogEntry,
  type AuditLogListParams,
} from "@/api/admin";
import { formatDateTime } from "@/utils/format";

export function AdminAuditLog() {
  const [resourceType, setResourceType] = useState("");
  const [resourceID, setResourceID] = useState("");
  const [action, setAction] = useState("");
  const [actorUserID, setActorUserID] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const params: AuditLogListParams = useMemo(() => {
    const p: AuditLogListParams = { limit: 200 };
    if (resourceType.trim()) p.resource_type = resourceType.trim();
    if (resourceID.trim()) p.resource_id = resourceID.trim();
    if (action.trim()) p.action = action.trim();
    const uid = Number(actorUserID.trim());
    if (actorUserID.trim() && Number.isFinite(uid) && uid > 0)
      p.actor_user_id = uid;
    return p;
  }, [resourceType, resourceID, action, actorUserID]);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["admin-audit-log", params],
    queryFn: () => listAdminAuditLog(params),
    // Same cadence + reasoning as the provider-logs page — operator
    // toggles auto-refresh only when actively investigating.
    refetchInterval: autoRefresh ? 5_000 : false,
  });

  const items = data?.items ?? [];

  return (
    <div className="stack-16" style={{ padding: "0 0 32px" }}>
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "flex-end" }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Audit log</h1>
          <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
            Cross-tenant view of every audit_log row. Filter by resource,
            action, or actor — click a row to inspect before/after JSON.
          </div>
        </div>
        <div className="row gap-12">
          <label
            className="row gap-8"
            style={{ alignItems: "center", fontSize: 12, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>
          <button
            className="btn ghost sm"
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card flat" style={{ padding: 12 }}>
        <div className="row gap-12" style={{ flexWrap: "wrap" }}>
          <FilterInput
            label="Resource type"
            placeholder="e.g. payout, bank_account"
            value={resourceType}
            onChange={setResourceType}
          />
          <FilterInput
            label="Resource ID"
            placeholder="e.g. 518"
            value={resourceID}
            onChange={setResourceID}
          />
          <FilterInput
            label="Action"
            placeholder="e.g. payout.admin_approve"
            value={action}
            onChange={setAction}
          />
          <FilterInput
            label="Actor user ID"
            placeholder="e.g. 30"
            value={actorUserID}
            onChange={setActorUserID}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card flat" style={{ overflow: "auto" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 44 }} />
              <th>Time</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Actor</th>
              <th>IP</th>
              <th className="mono">Request ID</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="dim" style={{ padding: 20 }}>
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td
                  colSpan={7}
                  style={{ padding: 20, color: "var(--neg)" }}
                >
                  Failed to load audit log. Try refresh.
                </td>
              </tr>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <tr>
                <td colSpan={7} className="dim" style={{ padding: 20 }}>
                  No audit-log rows match the current filters.
                </td>
              </tr>
            )}
            {items.map((row) => (
              <AuditRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="dim" style={{ fontSize: 11 }}>
        {items.length} row{items.length === 1 ? "" : "s"} shown · newest first
        {data && items.length >= data.limit && (
          <>
            {" "}· hit the page cap ({data.limit}) — narrow the filters to
            see older rows
          </>
        )}
      </div>
    </div>
  );
}

function FilterInput({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ flex: "1 1 180px", minWidth: 160 }}>
      <label
        style={{
          display: "block",
          fontSize: 10,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <input
        className="input"
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%" }}
      />
    </div>
  );
}

// Heuristic tone for an action label — green for approves/successes,
// red for rejects/failures, neutral otherwise. The classification reads
// from the action string suffix so it stays stable as new action codes
// are added.
function actionTone(action: string): "" | "pos" | "neg" | "warn" | "info" {
  if (action.includes("reject") || action.includes("fail")) return "neg";
  if (action.includes("approve") || action.includes("success")) return "pos";
  if (action.includes("create") || action.includes("ingest")) return "info";
  if (action.includes("dispatch") || action.includes("reconcile")) return "info";
  return "";
}

function AuditRow({ row }: { row: AdminAuditLogEntry }) {
  const [open, setOpen] = useState(false);

  // Stringify before/after for the JSON drop-down. The backend renders
  // null columns as the JSON null literal so the consumer doesn't need
  // a special-case branch.
  const beforeJson = useMemo(
    () => JSON.stringify(row.before, jsonReplacer, 2),
    [row.before],
  );
  const afterJson = useMemo(
    () => JSON.stringify(row.after, jsonReplacer, 2),
    [row.after],
  );

  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <td style={{ textAlign: "center" }} className="dim mono">
          {open ? "▾" : "▸"}
        </td>
        <td className="dim" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
          {formatDateTime(row.created_at)}
        </td>
        <td>
          <Pill tone={actionTone(row.action)}>{row.action}</Pill>
        </td>
        <td>
          <span className="mono">
            {row.resource_type}
            {row.resource_id ? ` #${row.resource_id}` : ""}
          </span>
        </td>
        <td>
          {row.actor_user_id != null ? (
            <>
              {/* Show the resolved email when the backend's user-directory
                  enrichment found one; otherwise fall back to the numeric
                  id so the row never renders as anonymous when an actor
                  was actually stamped. */}
              {row.actor_email ? (
                <span>{row.actor_email}</span>
              ) : (
                <span className="mono">#{row.actor_user_id}</span>
              )}
              {row.actor_role && (
                <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
                  · {row.actor_role}
                </span>
              )}
            </>
          ) : (
            <span className="dim">system</span>
          )}
        </td>
        <td className="mono" style={{ fontSize: 11 }}>
          {row.ip ?? <span className="dim">—</span>}
        </td>
        <td className="mono" style={{ fontSize: 11 }}>
          {row.request_id ?? <span className="dim">—</span>}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ background: "var(--bg-sunk)", padding: 0 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0,
              }}
            >
              <JsonBlock title="Before" body={beforeJson} />
              <JsonBlock title="After" body={afterJson} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function JsonBlock({ title, body }: { title: string; body: string }) {
  return (
    <div
      style={{
        padding: 12,
        borderRight: title === "Before" ? "1px solid var(--hair)" : undefined,
      }}
    >
      <div
        className="dim"
        style={{
          fontSize: 10,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <pre
        className="mono"
        style={{
          margin: 0,
          fontSize: 11,
          lineHeight: 1.5,
          color: "var(--ink-2)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 320,
          overflow: "auto",
        }}
      >
        {body}
      </pre>
    </div>
  );
}

// bigint values (amount_minor) can leak through json-bigint parse into the
// before/after blob; JSON.stringify can't serialise BigInt natively, so we
// coerce to a string with a trailing 'n' marker the way most JSON debuggers
// render them.
function jsonReplacer(_key: string, value: unknown): unknown {
  if (typeof value === "bigint") return value.toString() + "n";
  return value;
}
